// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/ConfirmModal.module.css';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Message} from '@app/features/channel/components/Message';
import Channels from '@app/features/channel/state/Channels';
import {Message as MessageModel} from '@app/features/messaging/models/Message';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import type {ModalProps} from '@app/features/ui/utils/ModalUtils';
import {MessagePreviewContext} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useMemo, useRef, useState} from 'react';

interface ConfirmModalCheckboxProps {
	checked?: boolean;
	onChange?: (checked: boolean) => void;
}

type ConfirmModalPrimaryVariant = 'primary' | 'danger-primary';
type ConfirmModalProps =
	| {
			title: React.ReactNode;
			description: React.ReactNode;
			message?: MessageModel;
			primaryText: React.ReactNode;
			primaryVariant?: ConfirmModalPrimaryVariant;
			secondaryText?: React.ReactNode | false;
			size?: ModalProps['size'];
			onPrimary: (checkboxChecked?: boolean) => Promise<void> | void;
			onSecondary?: (checkboxChecked?: boolean) => void;
			checkboxContent?: React.ReactElement<ConfirmModalCheckboxProps>;
			hideCloseButton?: boolean;
	  }
	| {
			title: React.ReactNode;
			description: React.ReactNode;
			message?: MessageModel;
			primaryText?: never;
			primaryVariant?: never;
			secondaryText?: React.ReactNode | false;
			size?: ModalProps['size'];
			onPrimary?: never;
			onSecondary?: (checkboxChecked?: boolean) => void;
			checkboxContent?: React.ReactElement<ConfirmModalCheckboxProps>;
			hideCloseButton?: boolean;
	  };
export const ConfirmModal = observer(
	({
		title,
		description,
		message,
		primaryText,
		primaryVariant = 'primary',
		secondaryText,
		size = 'small',
		onPrimary,
		onSecondary,
		checkboxContent,
		hideCloseButton,
	}: ConfirmModalProps) => {
		const {t} = useLingui();
		const [submitting, setSubmitting] = useState(false);
		const [checkboxChecked, setCheckboxChecked] = useState(false);
		const initialFocusRef = useRef<HTMLButtonElement | null>(null);
		const previewBehaviorOverrides = useMemo(
			() => ({
				isEditing: false,
				isHighlight: false,
				disableContextMenu: true,
				disableContextMenuTracking: true,
				contextMenuOpen: false,
			}),
			[],
		);
		const messageSnapshot = useMemo(() => {
			if (!message) return undefined;
			return new MessageModel(message.toJSON());
		}, [message?.id]);
		const handlePrimaryClick = useCallback(async () => {
			if (!onPrimary) {
				return;
			}
			setSubmitting(true);
			try {
				await onPrimary(checkboxChecked);
				ModalCommands.pop();
			} finally {
				setSubmitting(false);
			}
		}, [onPrimary, checkboxChecked]);
		const handleSecondaryClick = useCallback(() => {
			if (onSecondary) {
				onSecondary(checkboxChecked);
			}
			ModalCommands.pop();
		}, [onSecondary, checkboxChecked]);
		return (
			<Modal.Root size={size} initialFocusRef={initialFocusRef} centered>
				<Modal.Header title={title} hideCloseButton={hideCloseButton} />
				<Modal.Content>
					<Modal.ContentLayout>
						<Modal.Description>{description}</Modal.Description>
						{React.isValidElement(checkboxContent) &&
							React.cloneElement(checkboxContent, {
								checked: checkboxChecked,
								onChange: (value: boolean) => setCheckboxChecked(value),
							})}
						{messageSnapshot && (
							<div className={styles.messagePreview}>
								<Message
									channel={Channels.getChannel(messageSnapshot.channelId)!}
									message={messageSnapshot}
									previewContext={MessagePreviewContext.LIST_POPOUT}
									removeTopSpacing={true}
									behaviorOverrides={previewBehaviorOverrides}
								/>
							</div>
						)}
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer className={styles.footer}>
					{secondaryText !== false && (
						<Button onClick={handleSecondaryClick} variant="secondary">
							{secondaryText ?? t`Cancel`}
						</Button>
					)}
					{onPrimary && primaryText && (
						<Button onClick={handlePrimaryClick} submitting={submitting} variant={primaryVariant} ref={initialFocusRef}>
							{primaryText}
						</Button>
					)}
				</Modal.Footer>
			</Modal.Root>
		);
	},
);
